import { useEffect, useState } from "react";
import { Lock, Check, Sparkles, Zap, Star, Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import { apiFetch } from "@/lib/jwt-auth-client";
import { useAuth } from "@/components/auth/AuthProvider";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/Dialog";
import { usePaywallModal } from "@/store/usePaywallModal";
import { useAuthModal } from "@/store/useAuthModal";
import { trackEvent, trackPaywallViewed } from "@/shared/lib/gtm";

// ─── Per-plan highlights shown inside the modal ────────────────────────────

const PLAN_HIGHLIGHTS: Record<"starter" | "pro" | "enterprise", string[]> = {
  starter: [
    "1,500 credits/month",
    "Pipeline execution",
    "Video: Fast (720p)",
    "2 concurrent agents · 3 brands",
  ],
  pro: [
    "5,000 credits/month",
    "All AI models",
    "Video: Standard (1080p)",
    "10 agents · 15 brands",
    "Credit top-ups",
  ],
  enterprise: [
    "15,000 credits/month",
    "Unlimited agents & brands",
    "4K video + all tiers",
    "Dedicated support",
  ],
};

// ─── Per-plan static config ────────────────────────────────────────────────

const PLAN_CONFIG = {
  starter: {
    name: "Starter",
    monthlyPrice: 49,
    annualMonthlyPrice: 39,
    annualTotal: 468,
    Icon: Zap,
    ctaLabel: "Upgrade to Starter",
    trialNote: null,
    checkoutTierMonthly: "starter_monthly" as const,
    checkoutTierAnnual: "starter_annual" as const,
    accent: {
      bar: "from-primary to-primary/70",
      badge: "bg-primary/15 text-primary",
      icon: "bg-primary/15 text-primary ring-primary/20",
      check: "text-primary",
      btn: "bg-primary hover:bg-primary/90 text-primary-foreground",
    },
  },
  pro: {
    name: "Professional",
    monthlyPrice: 149,
    annualMonthlyPrice: 119,
    annualTotal: 1428,
    Icon: Star,
    ctaLabel: "Upgrade to Professional",
    trialNote: "14-day free trial · No card required",
    checkoutTierMonthly: "pro_monthly" as const,
    checkoutTierAnnual: "pro_annual" as const,
    accent: {
      bar: "from-primary to-primary/70",
      badge: "bg-primary/15 text-primary",
      icon: "bg-primary/15 text-primary ring-primary/20",
      check: "text-primary",
      btn: "bg-primary hover:bg-primary/90 text-primary-foreground",
    },
  },
  enterprise: {
    name: "Enterprise",
    monthlyPrice: 399,
    annualMonthlyPrice: 319,
    annualTotal: 3828,
    Icon: Building2,
    ctaLabel: "Upgrade to Enterprise",
    trialNote: null,
    checkoutTierMonthly: "enterprise_monthly" as const,
    checkoutTierAnnual: "enterprise_annual" as const,
    accent: {
      bar: "from-primary to-primary/70",
      badge: "bg-primary/15 text-primary",
      icon: "bg-primary/15 text-primary ring-primary/20",
      check: "text-primary",
      btn: "bg-primary hover:bg-primary/90 text-primary-foreground",
    },
  },
} as const;

// ─── Component ─────────────────────────────────────────────────────────────

export function PaywallModal() {
  const { isOpen, featureName, requiredPlan, featureDescription, close } = usePaywallModal();
  const { data: session } = useAuth();

  const [annual, setAnnual] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const plan = PLAN_CONFIG[requiredPlan];
  const highlights = PLAN_HIGHLIGHTS[requiredPlan];
  const price = annual ? plan.annualMonthlyPrice : plan.monthlyPrice;
  const tier = annual ? plan.checkoutTierAnnual : plan.checkoutTierMonthly;
  const { accent } = plan;
  const PlanIcon = plan.Icon;

  useEffect(() => {
    if (!isOpen) return;
    trackPaywallViewed({
      featureName,
      requiredPlan,
    });
  }, [featureName, isOpen, requiredPlan]);

  function handleClose() {
    close();
    setError(null);
    setAnnual(false);
  }

  async function handleUpgrade() {
    setError(null);

    if (!session?.user) {
      handleClose();
      useAuthModal.getState().openSignup(`Sign in to unlock ${featureName}`);
      return;
    }

    setIsLoading(true);
    try {
      trackEvent("begin_checkout", {
        ecommerce: { items: [{ item_name: tier }] },
      });
      const res = await apiFetch("/stripe/checkout/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tier }),
      });
      const payload = (await res.json()) as { url?: string; error?: string };
      if (!res.ok || !payload.url) {
        setError(payload.error ?? "Checkout failed. Please try again.");
        return;
      }
      window.location.href = payload.url;
    } catch {
      setError("Unable to start checkout right now.");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent className="sm:max-w-sm bg-card/95 backdrop-blur-[16px] p-0 shadow-ambient-lg overflow-hidden">
        <DialogTitle className="sr-only">
          Upgrade to {plan.name} to unlock {featureName}
        </DialogTitle>

        {/* Gradient accent bar */}
        <div className={`h-0.5 w-full bg-gradient-to-r ${accent.bar}`} />

        <div className="px-6 sm:px-8 pt-7 pb-8 space-y-5">
          {/* Icon + header */}
          <div className="flex items-start gap-4">
            <div
              className={`mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ring-4 ${accent.icon}`}
            >
              <Lock className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div
                className={`ds-micro-strong mb-1.5 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 ${accent.badge}`}
              >
                <PlanIcon className="h-3 w-3" />
                {plan.name} feature
              </div>
              <h2 className="ds-title text-foreground">
                Unlock {featureName}
              </h2>
              {featureDescription ? (
                <p className="ds-body-sm mt-1 text-muted-foreground">{featureDescription}</p>
              ) : null}
            </div>
          </div>

          {/* Plan highlights */}
          <div className="rounded-xl bg-muted/50 px-4 py-4 space-y-2.5">
            <p className="ds-label uppercase text-muted-foreground">
              What you get on {plan.name}
            </p>
            {highlights.map((feat) => (
              <div key={feat} className="flex items-center gap-2.5">
                <Check className={`h-3.5 w-3.5 shrink-0 ${accent.check}`} />
                <span className="ds-body-sm text-foreground/80">{feat}</span>
              </div>
            ))}
          </div>

          {/* Billing toggle + price */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span
                className={`ds-caption ${!annual ? "text-foreground" : "text-muted-foreground"}`}
              >
                Monthly
              </span>
              <button
                type="button"
                onClick={() => setAnnual((v) => !v)}
                aria-label="Toggle annual billing"
                className={`relative h-5 w-9 rounded-full transition-colors ${annual ? "bg-primary" : "bg-muted"}`}
              >
                <span
                  className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${annual ? "translate-x-4" : "translate-x-0.5"}`}
                />
              </button>
              <span
                className={`ds-caption ${annual ? "text-foreground" : "text-muted-foreground"}`}
              >
                Annual
              </span>
              {annual ? (
                <span className="ds-micro-strong rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-emerald-400">
                  save 20%
                </span>
              ) : null}
            </div>
            <div className="text-right">
              <span className="ds-value text-foreground">${price}</span>
              <span className="ds-caption text-muted-foreground">/mo</span>
              {annual ? (
                <p className="ds-micro text-muted-foreground">
                  billed ${plan.annualTotal.toLocaleString()}/yr
                </p>
              ) : null}
            </div>
          </div>

          {/* CTA */}
          <div className="space-y-2">
            <button
              onClick={handleUpgrade}
              disabled={isLoading}
              className={`ds-button-label w-full rounded-xl px-4 py-3 transition-all active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 ${accent.btn}`}
            >
              {isLoading ? "Redirecting…" : plan.ctaLabel}
            </button>
            {plan.trialNote ? (
              <p className="ds-caption flex items-center justify-center gap-1.5 text-center text-muted-foreground">
                <Sparkles className="h-3 w-3 text-primary" />
                {plan.trialNote}
              </p>
            ) : null}
            {error ? <p className="ds-caption text-center text-destructive">{error}</p> : null}
          </div>

          {/* Footer links */}
          <p className="ds-caption text-center text-muted-foreground">
            <Link
              to="/pricing"
              onClick={handleClose}
              className="text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
            >
              See all plans
            </Link>
            {" · "}
            <button
              type="button"
              onClick={handleClose}
              className="text-muted-foreground transition-colors hover:text-foreground"
            >
              Maybe later
            </button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
